import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { api } from '../../utils/api';
import { Users, Megaphone, TrendingUp, Activity } from 'lucide-react';

const AdminOverview = () => {
  const [stats, setStats] = useState({
    totalStaff: 0,
    activeStaff: 0,
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalLeads: 0,
    recentActivities: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [staffData, campaignsData, logsData] = await Promise.all([
        api.getStaff(),
        api.getCampaigns(),
        api.getActivityLogs(0, 10)
      ]);

      const activeStaff = staffData.staff.filter(s => s.enabled).length;
      const activeCampaigns = campaignsData.campaigns.filter(c => c.enabled).length;
      const totalLeads = campaignsData.campaigns.reduce((sum, c) => sum + c.lead_count, 0);

      setStats({
        totalStaff: staffData.staff.length,
        activeStaff,
        totalCampaigns: campaignsData.campaigns.length,
        activeCampaigns,
        totalLeads,
        recentActivities: logsData.logs.length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Staff',
      value: stats.totalStaff,
      subtitle: `${stats.activeStaff} active`,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Campaigns',
      value: stats.totalCampaigns,
      subtitle: `${stats.activeCampaigns} active`,
      icon: Megaphone,
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      title: 'Total Leads',
      value: stats.totalLeads,
      subtitle: 'Across all campaigns',
      icon: TrendingUp,
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      title: 'Recent Activities',
      value: stats.recentActivities,
      subtitle: 'Last 10 actions',
      icon: Activity,
      color: 'text-orange-600',
      bg: 'bg-orange-50'
    }
  ];

  if (loading) {
    return <div className="text-center py-8">Loading overview...</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1" data-testid="overview-title">Dashboard Overview</h2>
        <p className="text-slate-600">Welcome to your admin dashboard</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="card-hover" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-2" data-testid={`stat-value-${stat.title.toLowerCase().replace(' ', '-')}`}>{stat.value}</p>
                    <p className="text-xs text-slate-500 mt-1">{stat.subtitle}</p>
                  </div>
                  <div className={`${stat.bg} ${stat.color} p-3 rounded-lg`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Info</CardTitle>
          <CardDescription>System status and quick links</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm font-medium text-slate-700">System Status</span>
              <span className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded-full">Active</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm font-medium text-slate-700">Database</span>
              <span className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded-full">Connected</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOverview;
